/**
 * Consolidated role formatting and normalization utilities
 */

import { toLower } from './stringFormatting'

/**
 * Valid role types in the application
 */
const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'shift_lead', label: 'Shift Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' }
]

export const VALID_ROLES = new Set(ROLE_OPTIONS.map((option) => option.value))
export const ROLE_OPTIONS_EXPORT = ROLE_OPTIONS

const LEGACY_ROLE_ALIASES = Object.freeze({
  supervisor: 'shift_lead'
})

/**
 * Normalize a role to its canonical form (lowercase, with aliases resolved)
 * @param {string} value - Raw role value
 * @returns {string} Canonical role (may not be in VALID_ROLES)
 */
export const normalizeRoleCanonical = (value) => {
  const normalized = toLower(value)
  if (LEGACY_ROLE_ALIASES[normalized]) return LEGACY_ROLE_ALIASES[normalized]
  return normalized
}

/**
 * Normalize a role for storage in database
 * Returns a valid role or 'employee' as fallback
 * @param {string} value - Raw role value
 * @returns {string} Valid role value
 */
export const normalizeRoleForStorage = (value) => {
  const normalized = normalizeRoleCanonical(value)
  return VALID_ROLES.has(normalized) ? normalized : 'employee'
}

/**
 * Normalize a role for display in forms
 * Handles both legacy aliases and current valid roles
 * @param {string} value - Raw role value
 * @returns {string} Valid role value for forms
 */
export const normalizeRoleForForm = (value) => {
  const normalized = normalizeRoleCanonical(value)
  if (VALID_ROLES.has(normalized)) return normalized
  if (VALID_ROLES.has(value)) return value
  return 'employee'
}

/**
 * Format a role for display (human-readable label)
 * e.g., 'shift_lead' → 'Shift Lead'
 * @param {string} value - Raw role value
 * @returns {string} Display label
 */
export const formatRoleLabel = (value) => {
  const normalized = normalizeRoleForStorage(value)
  if (!normalized) return 'N/A'
  if (normalized === 'shift_lead') return 'Shift Lead'
  return normalized
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}
